import { useTranslations } from 'gt-react'
import { BookOpen } from 'lucide-react'
import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { helpCommandValue } from '../command-value'
import { type HelpSearchHit, MIN_QUERY_LENGTH, searchHelp } from '../search'
import { useHelpDocs } from '../use-help'

/**
 * De groep "Handleiding" in Ctrl+K (spec §7.2). De tekst laadt bij de eerste zoekopdracht van twee
 * tekens (spec §8), niet bij het openen: wie alleen wil navigeren, haalt niets op. Daarna zoekt hij
 * op de actuele vraag (niet uitgesteld).
 *
 * Geen `forceMount`: zo'n item registreert cmdk niet (cmdk 1.1.1), en dan kiest Enter niets. De
 * `value` komt uit `helpCommandValue` (die de vraag bevat en onder een passend scherm scoort).
 */
export function HelpCommandResults({
  query,
  onPick,
}: {
  query: string
  onPick: (hit: HelpSearchHit) => void
}) {
  const t = useTranslations()
  const docs = useHelpDocs(query.trim().length >= MIN_QUERY_LENGTH)
  const hits = useMemo(() => (docs.data ? searchHelp(docs.data, query, 6) : []), [docs.data, query])

  if (hits.length === 0) return null

  return (
    <CommandGroup heading={t('help.title')}>
      {hits.map((hit, rank) => (
        <CommandItem
          key={`${hit.id}#${hit.anchor}`}
          value={helpCommandValue(rank, query)}
          onSelect={() => onPick(hit)}
          className="flex items-center gap-2 px-2 py-1.5"
        >
          <BookOpen className="h-4 w-4" />
          <span className="truncate">
            {hit.title}
            {hit.heading ? ` › ${hit.heading}` : ''}
          </span>
          <Badge variant="secondary" className="ml-auto text-[10px]">
            {t('help.title')}
          </Badge>
        </CommandItem>
      ))}
    </CommandGroup>
  )
}
